import React from "react";
import { TrendingUp } from "lucide-react";
import { CollapsibleSection } from "./CollapsibleSection";
import { ProblematicPattern } from "../../../../../services/ai/types/ScanHistory.types";

interface PatternInsightsProps {
  patterns: ProblematicPattern[];
}

const FrequencyBadge = ({
  frequency
}: {
  frequency: number;
}): React.ReactElement => (
  <span
    className="inline-flex items-center rounded-full bg-[hsl(var(--warning)/0.15)] px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--warning))]"
    aria-label={`Seen in ${frequency} sessions`}
  >
    {frequency} sessions
  </span>
);

const PatternItem = ({
  pattern
}: {
  pattern: ProblematicPattern;
}): React.ReactElement => (
  <div className="rounded-md bg-card p-2.5">
    <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
      <p className="flex-1 text-xs font-semibold text-foreground">
        {pattern.description}
      </p>
      <FrequencyBadge frequency={pattern.frequency} />
    </div>
    <p className="text-xs text-muted-foreground">{pattern.suggestion}</p>
  </div>
);

export const PatternInsights = ({
  patterns
}: PatternInsightsProps): React.ReactElement | null => {
  if (patterns.length === 0) {
    return (
      <section
        className="my-3 rounded-lg border border-border p-3"
        aria-label="AI pattern insights"
      >
        <div className="flex items-center gap-2">
          <TrendingUp
            className="size-4 flex-shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <span className="text-sm font-semibold text-foreground">
            AI Insights
          </span>
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          No patterns detected yet. Patterns will appear after multiple scan
          sessions.
        </p>
      </section>
    );
  }

  return (
    <CollapsibleSection
      icon={
        <TrendingUp
          className="size-4 flex-shrink-0 text-[hsl(var(--warning))]"
          aria-hidden="true"
        />
      }
      title={`AI Insights: ${patterns.length} pattern${patterns.length !== 1 ? "s" : ""} detected`}
      ariaLabel="AI pattern insights"
    >
      {patterns.map((pattern, index) => (
        <PatternItem
          key={`${pattern.patternType}-${pattern.areaName ?? pattern.timeSlot ?? ""}-${index}`}
          pattern={pattern}
        />
      ))}
    </CollapsibleSection>
  );
};
